import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command'
import clsx from 'clsx'
import { BaseFieldProps } from './types'
import { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/utils'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'
import { Badge } from '@/components/shadcn/badge'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconInformationOutline } from '@icons/IconInformationOutline'
import { DocumentationButton } from './DocumentationButton'

interface MultiSelectComboboxFieldProps extends BaseFieldProps {
  applyButton?: React.ReactNode
  popoverContentClassName?: string
}

interface SelectedBadgesProps {
  selectedValues: (string | number)[]
  variant: BaseFieldProps['variant']
  placeholder: string
  getSelectedLabel: (value: string | number) => string
}

const SelectedBadges = ({ selectedValues, variant, placeholder, getSelectedLabel }: SelectedBadgesProps) => {
  const isInlineCompact = variant === 'inline'
  const badgeLabelClassName = cn('inline-block truncate font-normal', {
    'max-w-20': variant === 'row' || variant === 'inline',
    'max-w-24': variant !== 'row' && variant !== 'inline',
  })
  const selectionKey = useMemo(() => selectedValues.map((value) => String(value)).join('\u0001'), [selectedValues])
  const selectedLabels = useMemo(
    () => selectedValues.map((value) => getSelectedLabel(value)),
    [selectedValues, getSelectedLabel]
  )
  const [visibleBadgeCount, setVisibleBadgeCount] = useState(() => {
    if (selectedLabels.length === 0) return 0
    return isInlineCompact ? Math.min(2, selectedLabels.length) : 1
  })
  const badgeAreaRef = useRef<HTMLDivElement | null>(null)
  const [badgeAreaWidth, setBadgeAreaWidth] = useState(0)
  const badgeMeasureRefs = useRef<(HTMLSpanElement | null)[]>([])
  const overflowMeasureRefs = useRef<Record<number, HTMLSpanElement | null>>({})
  const [remeasureTick, setRemeasureTick] = useState(0)
  const remeasureAttemptsForSelectionRef = useRef(0)

  const scheduleRemeasure = useCallback(() => {
    remeasureAttemptsForSelectionRef.current += 1
    if (remeasureAttemptsForSelectionRef.current <= 12) {
      requestAnimationFrame(() => {
        setRemeasureTick((tick) => tick + 1)
      })
    }
  }, [])

  useEffect(() => {
    if (!badgeAreaRef.current || typeof ResizeObserver === 'undefined') return

    const element = badgeAreaRef.current
    const observer = new ResizeObserver(() => {
      setBadgeAreaWidth(element.clientWidth)
    })

    setBadgeAreaWidth(element.clientWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useLayoutEffect(() => {
    const total = selectedLabels.length

    if (isInlineCompact) {
      setVisibleBadgeCount(Math.min(2, total))
      return
    }

    if (total === 0) {
      setVisibleBadgeCount(0)
      return
    }

    // Prefer live DOM width: badgeAreaWidth is only set after paint via ResizeObserver, so on the
    // first layout pass state is still 0 and we would skip measuring — leaving visibleBadgeCount at
    // its initial value (all labels) for prepopulated fields. clientWidth is valid in useLayoutEffect.
    const areaWidth = badgeAreaRef.current?.clientWidth ?? 0
    if (areaWidth <= 0) {
      scheduleRemeasure()
      if (total > 1) {
        setVisibleBadgeCount(1)
      }
      return
    }

    const badgeWidths = selectedLabels.map((_, index) => badgeMeasureRefs.current[index]?.offsetWidth ?? 0)
    const overflowWidths: Record<number, number> = {}
    for (let hiddenCount = 1; hiddenCount <= total; hiddenCount += 1) {
      overflowWidths[hiddenCount] = overflowMeasureRefs.current[hiddenCount]?.offsetWidth ?? 0
    }

    const sumBadgeWidths = badgeWidths.reduce((sum, width) => sum + width, 0)
    const overflowWidthsReady = total <= 1 || Object.values(overflowWidths).some((width) => width > 0)

    // If every measured width is still 0 (popover open animation, fonts, or visibility affecting
    // layout), the fit loop below would accept all badges. Defer one frame and cap visibly until
    // measurements exist.
    if (sumBadgeWidths === 0 || !overflowWidthsReady) {
      scheduleRemeasure()
      if (total > 1) {
        setVisibleBadgeCount(1)
      }
      return
    }

    remeasureAttemptsForSelectionRef.current = 0

    let usedWidth = 0
    let nextVisibleCount = 0
    for (let index = 0; index < total; index += 1) {
      const remaining = total - (index + 1)
      const overflowWidth = remaining > 0 ? (overflowWidths[remaining] ?? 0) : 0
      const badgeWidth = badgeWidths[index] ?? 0
      if (usedWidth + badgeWidth + overflowWidth > areaWidth) break
      usedWidth += badgeWidth
      nextVisibleCount = index + 1
    }

    if (nextVisibleCount === 0 && total > 0) {
      const counterOnlyWidth = overflowWidths[total] ?? 0
      nextVisibleCount = counterOnlyWidth <= areaWidth ? 0 : 1
    }

    setVisibleBadgeCount(nextVisibleCount)
  }, [badgeAreaWidth, isInlineCompact, selectedLabels, remeasureTick, scheduleRemeasure])

  useEffect(() => {
    remeasureAttemptsForSelectionRef.current = 0
  }, [selectionKey])

  if (selectedLabels.length === 0) {
    return (
      <div ref={badgeAreaRef} className="flex min-w-0 flex-1 items-center text-left">
        <span className="block truncate leading-none">{placeholder}</span>
      </div>
    )
  }

  if (isInlineCompact) {
    const hasOverflow = selectedLabels.length > 2
    return (
      <div ref={badgeAreaRef} className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {selectedLabels.slice(0, 2).map((label, index) => (
          <Badge key={`${label}-${index}`} variant="outline" className="bg-muted">
            <span className={badgeLabelClassName}>{label}</span>
          </Badge>
        ))}
        {hasOverflow && <Badge variant="outline">+ {selectedLabels.length - 2}</Badge>}
      </div>
    )
  }

  const hiddenCount = Math.max(selectedLabels.length - visibleBadgeCount, 0)

  return (
    <div ref={badgeAreaRef} className="relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
      {selectedLabels.slice(0, visibleBadgeCount).map((label, index) => (
        <Badge key={`${label}-${index}`} variant="outline" className="bg-muted">
          <span className={badgeLabelClassName}>{label}</span>
        </Badge>
      ))}
      {hiddenCount > 0 && <Badge variant="outline">+ {hiddenCount}</Badge>}

      <div className="pointer-events-none absolute -z-10 whitespace-nowrap opacity-0">
        {selectedLabels.map((label, index) => (
          <span
            key={`measure-${label}-${index}`}
            ref={(element) => {
              badgeMeasureRefs.current[index] = element
            }}
            className="mr-1 inline-block"
          >
            <Badge variant="outline" className="bg-muted">
              <span className={badgeLabelClassName}>{label}</span>
            </Badge>
          </span>
        ))}
        {selectedLabels.map((_, index) => {
          const hidden = index + 1
          return (
            <span
              key={`measure-overflow-${hidden}`}
              ref={(element) => {
                overflowMeasureRefs.current[hidden] = element
              }}
              className="mr-1 inline-block"
            >
              <Badge variant="outline">+ {hidden}</Badge>
            </span>
          )
        })}
      </div>
    </div>
  )
}

export const MultiSelectComboboxField = ({
  field,
  formFieldName,
  control,
  variant,
  applyButton,
  popoverContentClassName,
}: MultiSelectComboboxFieldProps) => {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Memoize the filtered options to prevent recalculation on every render
  const filteredOptions = useMemo(() => {
    if (!field.items?.choices?.length) return []

    // Only filter if there's a search query
    if (!searchQuery.trim()) return field.items.choices

    const lowerQuery = searchQuery.toLowerCase()
    return field.items.choices.filter((option) => {
      const label = typeof option === 'string' || typeof option === 'number' ? String(option) : option.title
      return label.toLowerCase().includes(lowerQuery)
    })
  }, [field.items?.choices, searchQuery])

  // Reference for the parent container of virtualized items
  const [parentRef, setParentRef] = useState<HTMLDivElement | null>(null)

  // Setup virtualization with fixed item height to prevent overlapping
  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef,
    estimateSize: () => 36, // Fixed height for each item
    overscan: 5, // Number of items to render before/after the visible area
  })

  // Reset search query when popover closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
    }
  }, [open])

  // Get the display label for a selected value (stable for SelectedBadges memoization)
  const getSelectedLabel = useCallback(
    (value: string | number) => {
      if (value === '' || value === undefined || value === null || !field.items?.choices?.length)
        return String(value ?? '')

      const selectedOption = field.items.choices.find((option) => {
        const optionValue = typeof option === 'string' || typeof option === 'number' ? option : option.value
        return String(optionValue) === String(value)
      })

      return selectedOption
        ? typeof selectedOption === 'string' || typeof selectedOption === 'number'
          ? String(selectedOption)
          : selectedOption.title
        : String(value)
    },
    [field.items?.choices]
  )

  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => {
        const selectedValues = (formField.value as (string | number)[]) || []

        // Define the handler directly in the render function
        const handleSelect = (optionValue: string | number, isSelected: boolean) => {
          const newValue = [...selectedValues]
          if (isSelected) {
            // Remove if already selected (compare stringified to be robust)
            const index = newValue.findIndex((v) => String(v) === String(optionValue))
            if (index !== -1) newValue.splice(index, 1)
          } else {
            // Add if not selected
            newValue.push(optionValue)
          }
          formField.onChange(newValue)
        }

        const selectAllTargetOptions = searchQuery.trim() ? filteredOptions : (field.items?.choices ?? [])
        const selectAllTargetValues = selectAllTargetOptions.map((option) =>
          typeof option === 'string' || typeof option === 'number' ? option : option.value
        )
        const allTargetsSelected =
          selectAllTargetValues.length > 0 &&
          selectAllTargetValues.every((v) => selectedValues.some((s) => String(s) === String(v)))

        const handleSelectAll = () => {
          const merged = new Map(selectedValues.map((v) => [String(v), v]))
          for (const v of selectAllTargetValues) {
            merged.set(String(v), v)
          }
          formField.onChange(Array.from(merged.values()))
        }

        const fieldControl = (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                // className={cn('flex w-full items-center justify-between gap-2 rounded-sm', {
                className={cn('!grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2', {
                  'h-6 min-h-0 min-w-48 max-w-48 py-0': variant === 'row',
                  'h-6 min-h-0 border-none py-0': variant === 'inline',
                  'h-8 py-0': variant !== 'row' && variant !== 'inline',
                })}
                variant="input"
                role="combobox"
                aria-expanded={open}
                disabled={field.readOnly}
              >
                <SelectedBadges
                  selectedValues={selectedValues}
                  variant={variant}
                  placeholder={`Select ${field.title.toLowerCase()}`}
                  getSelectedLabel={getSelectedLabel}
                />
                <IconDownChevronOutline className="block size-4 shrink-0 self-center opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className={cn(
                'w-[var(--radix-popover-trigger-width)] p-0',
                variant === 'inline' && 'min-w-80',
                popoverContentClassName
              )}
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={`Search ${field.title.toLowerCase()}...`}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  <CommandEmpty>No options found.</CommandEmpty>
                  <CommandGroup>
                    {/* Virtualized list container */}
                    <div
                      ref={setParentRef}
                      className="max-h-[200px] overflow-auto"
                      style={{
                        height: Math.min(filteredOptions.length * 36, 200),
                        width: '100%',
                      }}
                    >
                      {/* Set the total height of the virtualizer */}
                      <div
                        style={{
                          height: `${rowVirtualizer.getTotalSize()}px`,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {/* Only render visible items */}
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                          const option = filteredOptions[virtualRow.index]
                          if (!option) return null // Safety check

                          const optionValue =
                            typeof option === 'string' || typeof option === 'number' ? option : option.value
                          const optionLabel =
                            typeof option === 'string' || typeof option === 'number' ? String(option) : option.title
                          const isSelected = selectedValues.some((v) => String(v) === String(optionValue))

                          return (
                            <Tooltip key={virtualRow.key}>
                              <TooltipTrigger asChild>
                                <div
                                  data-index={virtualRow.index}
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                  }}
                                >
                                  <CommandItem
                                    value={String(optionValue)}
                                    onSelect={() => handleSelect(optionValue, isSelected)}
                                    className="h-9" // Fixed height for each item
                                  >
                                    <IconCheckMarkOutline
                                      className={clsx('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                                    />
                                    <span className="truncate">{optionLabel}</span>
                                  </CommandItem>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-foreground text-xs text-offwhite">
                                <p>{optionLabel}</p>
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </div>
                    </div>
                  </CommandGroup>
                </CommandList>
                {(field.items?.choices?.length ?? 0) > 0 &&
                  (selectedValues.length > 0 || (selectAllTargetValues.length > 0 && !allTargetsSelected)) && (
                    <div className="flex justify-end gap-x-2 border-t p-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6"
                        disabled={allTargetsSelected || selectAllTargetValues.length === 0}
                        onClick={handleSelectAll}
                      >
                        <IconCheckMarkOutline className="mr-2 size-3" />
                        Select all
                      </Button>
                      {selectedValues.length > 0 && (
                        <Button size="sm" variant="outline" className="h-6" onClick={() => formField.onChange([])}>
                          <IconCloseOutline className="mr-2 size-3" />
                          Clear
                        </Button>
                      )}
                      {applyButton}
                    </div>
                  )}
              </Command>
            </PopoverContent>
          </Popover>
        )

        const isCompact = variant === 'row' || variant === 'inline'
        return (
          <FormItem
            className={clsx(
              'flex flex-1 flex-col',
              field.widget === 'hidden' && 'hidden',
              isCompact ? 'space-y-0' : ''
            )}
          >
            <div className={clsx('flex justify-start gap-2', { hidden: variant === 'inline' })}>
              <FormLabel className={variant === 'row' ? 'sr-only' : 'block'}>{field.title}</FormLabel>
              {!isCompact && <DocumentationButton documentation={field.documentation} />}
            </div>
            <FormDescription
              className={clsx({
                'sr-only': variant === 'row',
                hidden: variant === 'inline',
                'block text-xs': !isCompact,
              })}
            >
              {field.description}
            </FormDescription>
            {variant === 'row' ? (
              <div className="flex items-center gap-x-1 text-muted-foreground">
                <TooltipButton tooltip={field.title}>
                  <div className="shrink-0">
                    <IconInformationOutline className="size-4" />
                  </div>
                </TooltipButton>
                {fieldControl}
              </div>
            ) : (
              fieldControl
            )}
            {!isCompact && <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}
